/** One tab-local, classified nonsecret command intent; never a server ledger. */
import type { CompanyMethodCommandIntent, CompanySoftwareBillingScope } from "../api/companySoftwareBilling";

const KEY = "bw:company-method-intent:v1";
const UUID = /^(?!00000000-0000-0000-0000-000000000000$)[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HASH = /^[0-9a-f]{64}$/;
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === "object" && !Array.isArray(value);
const keys = (value: Record<string, unknown>, expected: string[]) => Object.keys(value).length === expected.length && expected.every(key => key in value);
const uuid = (value: unknown): value is string => typeof value === "string" && UUID.test(value);

export function sameCompanyBillingScope(left: CompanySoftwareBillingScope, right: CompanySoftwareBillingScope): boolean {
  return left.actorId === right.actorId && left.orgId === right.orgId && left.acceptanceId === right.acceptanceId && left.environment === right.environment;
}

function validIntent(value: unknown): value is CompanyMethodCommandIntent {
  if (!object(value) || !keys(value, ["version", "scope", "kind", "request"]) || value.version !== 1 || !object(value.scope) || !object(value.request)) return false;
  const scope = value.scope, request = value.request;
  if (!keys(scope, ["actorId", "orgId", "acceptanceId", "environment"]) || typeof scope.actorId !== "string" || !scope.actorId.trim()
    || scope.actorId.length > 255 || !uuid(scope.orgId) || !uuid(scope.acceptanceId)
    || !["sandbox", "production"].includes(String(scope.environment)) || request.environment !== scope.environment || !uuid(request.command_id)) return false;
  if (value.kind === "revoke") return keys(request, ["environment", "command_id", "consent_id"]) && uuid(request.consent_id);
  if (request.acceptance_id !== scope.acceptanceId || typeof request.reviewed_terms_fingerprint !== "string" || !HASH.test(request.reviewed_terms_fingerprint)) return false;
  if (value.kind === "setup") return keys(request, ["environment", "command_id", "acceptance_id", "consent_id", "reviewed_terms_fingerprint"]) && uuid(request.consent_id);
  return value.kind === "grant" && keys(request, ["environment", "command_id", "acceptance_id", "reviewed_terms_fingerprint", "disclosure_version_id",
    "reviewed_disclosure_sha256", "expected_consent_id", "expected_consent_sequence"])
    && uuid(request.disclosure_version_id) && typeof request.reviewed_disclosure_sha256 === "string" && HASH.test(request.reviewed_disclosure_sha256)
    && Number.isSafeInteger(request.expected_consent_sequence) && Number(request.expected_consent_sequence) >= 0
    && (request.expected_consent_id === null ? request.expected_consent_sequence === 0 : uuid(request.expected_consent_id) && Number(request.expected_consent_sequence) > 0);
}

function readRaw(): unknown {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw === null ? null : JSON.parse(raw);
  } catch { return null; }
}

export function clearCompanyMethodCommandIntent(): void {
  try { sessionStorage.removeItem(KEY); } catch { /* Storage unavailable; no server state changes. */ }
}

export function clearCompanyMethodCommandIntentForScope(scope: CompanySoftwareBillingScope): void {
  const value = readRaw();
  if (validIntent(value) && sameCompanyBillingScope(value.scope, scope)) clearCompanyMethodCommandIntent();
}

export function synchronizeCompanyMethodIntentActor(actorId: string | null): void {
  const value = readRaw();
  if (actorId === null || !validIntent(value) || value.scope.actorId !== actorId) clearCompanyMethodCommandIntent();
}

export function readCompanyMethodCommandIntent(scope: CompanySoftwareBillingScope): CompanyMethodCommandIntent | null {
  const value = readRaw();
  if (!validIntent(value) || !sameCompanyBillingScope(value.scope, scope)) {
    clearCompanyMethodCommandIntent();
    return null;
  }
  return value;
}

export function writeCompanyMethodCommandIntent(value: CompanyMethodCommandIntent): boolean {
  if (!validIntent(value)) return false;
  try {
    sessionStorage.setItem(KEY, JSON.stringify(value));
    return true;
  } catch { return false; }
}
